import { FormGroup } from "@angular/forms";
import { OPERATION, SupplierDocument } from "./supplier.model";

/** Tabs of the onboarding process */
export enum OnboardingTab {
  Home = 'HOME',
  DETAILS = 'DETAILS',
  ADDRESS = 'ADDRESS',
  CONTACTS = 'CONTACTS',
  BANK = 'BANK',
  TAX = 'TAX',
  DIVERSITY = 'DIVERSITY',
  DOCUMENTS = 'DOCUMENTS',
  COMPLETED = 'COMPLETE',
  INSURANCE = 'INSURANCE',
  EHS = 'EHS',
  DOMAIN = 'DOMAIN',
}

export enum OnboardingTabStatus {
  Initialized = 'Initialized',
  SavedStatus = 'Saved',
  SubmittedStatus = 'Submitted',
  RejectedStatus = 'Rejected'
}

export enum TaskStatus {
  New = 'New',
  Completed = 'Completed',
  Rejected = 'Rejected'
}

export enum TimeTrackTypes { 
  StartTime = 'StartTime',
  EndTime = 'EndTime',
  CompletedTime = 'CompletedTime'
}

export interface OnboardingTrainingDocument {
  title: string,
  url: string,
}

export class DiversityType {
  Code: string;
  Id: number;
  Name: string;
  Description: string;
}

export class SupplierDiversity {
  Operation: OPERATION;
  IsDiversityOwned: string;
  DiversityTypes: DiversitySelection[];
  OwnershipPercentage: number;
  IsCouncilCertified: string;
  CouncilCertifications: DiversitySelection[];
  IsGovtCertified: string;
  GovtCertifications: DiversitySelection[];
  DocumentIds: string[]
  Documents: SupplierDocument[];
}

export class DiversitySelection {
  code: string;
  percentage?: number;
  comment?: string;
  document?: SupplierDocument
}

export class AgencyType {
  Id: number;
  Name: string;
  Description?: string;
  Code: string
  DocumentType: string;
  Type: string;
}

export class DocumentType {
  documentTypeID: string;
  name: string;
  description: string;
  category: string;
}

export class Rejection {
  rejectionTab: string;
  rejectedReasons: string[];
  rejectionComment: string;
  rejectionAttachment: SupplierDocument;
}

//#region Supplier Onboarding Information
export class SupplierOnboardingData {
  referenceSystem: string;
  referenceIdentifier: string;
  name: string;
  supplierCode: string;
  properties: Properties;
  createdBy: string;
  createdTimeStamp: Date;
  isInitialOnboarding: boolean;
  currencyCode: string;
}

export interface Properties {
  supplierName?: string;
  supplierContactEmail?: string;
  details?: OnboardingAddressDetails;
  addresses?: (OnboardingAddress)[] | null;
  contacts?: (OnboardingContact)[] | null;
  documents?: (OnboardingDocument)[] | null;
  documentInformation?: OnboardingDocumentInformation;
  banks?: (OnboardingBank)[] | null;
  taxes?: (OnboardingTax)[] | null;
  completeRegistration?: CompleteRegistration;
}

export interface OnboardingAddressDetails {
  LegalName?: string;
  DBAName?: string;
  diversityDocumentTypes?: string[];
  Operation?: OPERATION;
  ID?: number;
  StageID?: string;
  Types?: string[];
  Street1?: string;
  Street2?: string;
  Street3?: string;
  City?: string;
  State?: string;
  CountryCode?: string;
  ZipCode?: string;
  Comment?: string;
  Documents?: string[];
  DocumentInfos?: OnboardingDocument[];
}

export interface OnboardingAddress {
  street1: string;
  city: string;
  state: string;
  zipCode: string;
  countryCode: string;
  types?: (string)[] | null;
}

export interface OnboardingContact {
  firstName: string;
  lastName: string;
  phone: string;
  fax: string;
  mobile?: string | null;
  email: string;
  comment: string;
  jobTitle: string;
  financeLead: boolean;
  types?: (string)[] | null;
}

export interface OnboardingDocument {
  DocumentType?: string;
  DocumentTypeDescription?: string;
  TypeID?: string;
  Templates?: string[];
  FileName?: string;
  FileNameGuid?: string;
  UploadedDate?: Date;
  SignedDate?: Date;
  TemplateId?: string;
  DocumentID?: string;
  DocumentVersion?: string;
  SignDateRequired?: boolean;
  ESignAgreementId?: string;
  ESignAgreementStatus?: string;
  IsRequestDocument?: boolean;
  EffectiveDate?: Date;
  ExpiryDate?: Date;
  ModifiedDate?: Date;
  Mode?: string,
  FormGroup?: FormGroup;
  AllowedExtensions?: string;
  ID?: number;
  File?: File;
  ValidationRequired?: boolean;
  IsDocumentAvailableForSupplier?: boolean;
  AssociatedCategoryLinkId?: string[];
  IsOptional?: boolean;
  IsOptionalList?: boolean;
  DocumentTypeList?: DocumentTypeInfo[]; 
}

export class DocumentTypeInfo {
  DocumentType : string;
  DocumentTypeDescription : string
  DocumentTypeID: number;
}

export interface OnboardingDocumentInformation {
  Documents?: OnboardingDocument[];
  Diversity: SupplierDiversity;
  DiversityDocuments?: OnboardingDocument[];
}

export interface OnboardingBank {
  bankName: string;
  bankLocalName: string;
  countryCode: string;
  abaNumber: string;
  accountNumber: string;
  last4AccountNumber: string;
  last4IntermediaryBankAccount: string;
  swiftCode: string;
  bankNickName: string;
  currencyCode: string;
  accountNumberExists: boolean;
  documents?: (string)[] | null;
  documentInfos?: (OnboardingDocumentInformation)[] | null;
}

export interface OnboardingTax {
  tax1099Box: string;
  taxName: string;
  w9Received: boolean;
  taxIDTypes: string;
  corporate: boolean;
  federalTaxID: string;
  last4FederalTaxID: string;
  vatNumber: string;
  doYouHaveVat: string;
  isNewRecord: boolean;
  federalTaxIDExists: boolean;
  documents?: (string)[] | null;
  documentInfos?: (DocumentInfosEntity)[] | null;
}

export interface DocumentInfosEntity {
  documentType: string;
  documentTypeDescription: string;
  typeID: number;
  fileName: string;
  fileNameGuid: string;
  documentID: string;
  uploadedDate: string;
  signedDate: string;
  documentVersion: string;
}


export class Address {
  types: string[];
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  countryCode: string;
  comments: string;
  documentInfos: Document[];
}

export class OnboardingDocumentType {
  Type: string;
  Mode: string;
}

export class Entity {
  Note: string;
  EntityName: string;
  CountryCode: string;
  NetTermCode: string;
  CurrencyCode: string;
  IncoTermCode: string;
}

export class SupplierOnboardingContact {
  FirstName: string;
  LastName: string;
  Email: string;
  LanguageCode: string;
  SupplierCode: string;
  IsPrimary: boolean;
  Roles: string[];
}

//  We use this interface to get all the supplier data regarding onboarding
export interface SupplierOnboardingInformation {
  regionCode: string,
  supplierType: number,
  onboardingType: string,
  supplierCode: string,
  primaryContactEmail: string,
  entityName: string,
  documentTypeIds: number[],
  documentTypes: OnboardingDocumentType[],
  onboardingStatus: string,
  statusDescription: string,
  rejectionReasons: string[],
  legalName: string,
  dbaName: string,
  ndaSignType: string,
  supplierOnboardingData: SupplierOnboardingData,
  isInitialOnboarding: boolean,
  primaryContactFirstName: string,
  primaryContactLastName: string,
  primaryContactType: string,
  primaryContactLanguageCode: string,
  isExtendedOnboarding: boolean,
  address: Address[],
  documentMatrix: Document[],
  entityMatrix: Entity[],
  contacts: SupplierOnboardingContact[],
  vendorCountry: string
}

//  We use this interface for saving onboarding information to the back-end
export interface SupplierInformation {
  ReferenceSystem?: string;
  ReferenceIdentifier?: string;
  Name?: string;
  SupplierCode?: string;
  Properties?: Properties;
  CreatedBy?: string;
  CreatedTimeStamp?: Date;
  IsInitialOnboarding?: boolean;
  CurrencyCode?: string;
}

export class CompleteRegistration {
  PaymentTerms: string;
  IncoTerms: string;
  NoPONoPayPolicy: boolean;
  CodeofConduct: boolean;
  PrivacyNotice: boolean;
  Note: string;
  // agreement:string;`
  constructor() {
    this.NoPONoPayPolicy = false;
    this.PrivacyNotice = false;
    this.CodeofConduct= false;
    this.PaymentTerms = "";
    this.IncoTerms = ""
    this.Note = "You have been listed as the primary contact for [Company Name] in Tesla's Vendor Portal. If you need to make any changes, please reach out to VendorChanges@tesla.com";
  }
}

export class SurveyRequest {
  Rating: number;
  Comments: string;
  SupplierCode: string;
}

export interface SupplierSurvey {
  rating: number;
  comments: string;
  supplierCode: string;
}

export class FormTemplate {
  id: number;
  formType: string;
  category: string;
  description: string;
  formJson: any;
  version: number;
}
//#endregion
